import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Video, Search, Play, Calendar, GraduationCap, Loader2, Lock, Plus, Trash2, X, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

// Función para extraer ID de YouTube de una URL
const getYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// Función para verificar si es una playlist de YouTube
const getYouTubePlaylistId = (url) => {
  if (!url) return null;
  const regExp = /[?&]list=([^#&?]+)/;
  const match = url.match(regExp);
  return match ? match[1] : null;
};

const GraduadosGrabaciones = () => {
  const { user, API } = useContext(AuthContext);
  const [grabaciones, setGrabaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [newGrabacion, setNewGrabacion] = useState({
    titulo: '',
    descripcion: '',
    url: '',
    duracion: '',
    categoria: 'conferencia'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadGrabaciones();
  }, []);

  const loadGrabaciones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/graduados/grabaciones`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGrabaciones(data.grabaciones || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGrabacion = async () => {
    if (!newGrabacion.titulo || !newGrabacion.url) {
      toast.error('Título y URL son requeridos');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/graduados/grabaciones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newGrabacion)
      });
      
      if (!response.ok) throw new Error('Error al guardar');
      
      toast.success('Grabación añadida exitosamente');
      setShowAddModal(false);
      setNewGrabacion({ titulo: '', descripcion: '', url: '', duracion: '', categoria: 'conferencia' });
      loadGrabaciones();
    } catch (error) {
      toast.error('Error al guardar la grabación');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGrabacion = async (id) => {
    if (!window.confirm('¿Eliminar esta grabación?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/graduados/grabaciones/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Error al eliminar');
      
      toast.success('Grabación eliminada');
      loadGrabaciones();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openVideoPlayer = (grabacion) => {
    setSelectedVideo(grabacion);
    setShowVideoModal(true);
  };

  const filteredGrabaciones = grabaciones.filter(g =>
    g.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categorias = [
    { value: 'conferencia', label: 'Conferencia', icon: '🎤' },
    { value: 'seminario', label: 'Seminario', icon: '📚' },
    { value: 'testimonio', label: 'Testimonio', icon: '✝️' },
    { value: 'capacitacion', label: 'Capacitación', icon: '🎓' },
    { value: 'especial', label: 'Evento Especial', icon: '⭐' },
    { value: 'playlist', label: 'Playlist/Serie', icon: '📋' }
  ];

  // Renderizar el reproductor embebido
  const renderEmbeddedPlayer = (url) => {
    const youtubeId = getYouTubeId(url);
    const playlistId = getYouTubePlaylistId(url);
    
    if (playlistId) {
      // Es una playlist de YouTube
      return (
        <iframe
          className="w-full aspect-video rounded-lg"
          src={`https://www.youtube.com/embed/videoseries?list=${playlistId}`}
          title="YouTube Playlist"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    } else if (youtubeId) {
      // Es un video individual de YouTube
      return (
        <iframe
          className="w-full aspect-video rounded-lg"
          src={`https://www.youtube.com/embed/${youtubeId}`}
          title="YouTube Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    } else {
      // URL externa - mostrar botón para abrir
      return (
        <div className="w-full aspect-video bg-gray-100 rounded-lg flex flex-col items-center justify-center">
          <Video className="w-16 h-16 text-gray-400 mb-4" />
          <p className="text-gray-600 mb-4">Este video no se puede reproducir dentro de la aplicación</p>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <ExternalLink className="w-4 h-4" />
            Abrir en nueva pestaña
          </a>
        </div>
      );
    }
  };

  if (!user?.es_graduado && user?.rol !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-10 h-10 text-purple-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Contenido Exclusivo</h2>
            <p className="text-gray-600">
              Las grabaciones son exclusivas para graduados del Instituto Bíblico Apostólico.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
            <Video className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Grabaciones Exclusivas</h1>
            <p className="text-gray-600 text-sm">Contenido especial para graduados</p>
          </div>
        </div>
        
        {user?.rol === 'admin' && (
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Añadir Grabación
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Buscar grabaciones..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Grabaciones Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : filteredGrabaciones.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'No se encontraron grabaciones' : 'Aún no hay grabaciones disponibles'}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Pronto se añadirá contenido exclusivo para graduados
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGrabaciones.map((grabacion) => {
            const youtubeId = getYouTubeId(grabacion.url);
            const playlistId = getYouTubePlaylistId(grabacion.url);
            
            return (
              <Card key={grabacion.id} className="hover:shadow-lg transition-shadow overflow-hidden group cursor-pointer" onClick={() => openVideoPlayer(grabacion)}>
                <CardContent className="p-0">
                  {/* Thumbnail */}
                  <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 h-40 flex items-center justify-center">
                    {youtubeId ? (
                      <img
                        src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`}
                        alt={grabacion.titulo}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Video className="w-12 h-12 text-gray-600" />
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-purple-600 ml-1" />
                      </div>
                    </div>
                    {grabacion.duracion && (
                      <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {grabacion.duracion}
                      </span>
                    )}
                    <span className="absolute top-2 left-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                      {playlistId ? '📋 Playlist' : (categorias.find(c => c.value === grabacion.categoria)?.label || 'Video')}
                    </span>
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-bold text-gray-800 mb-2 line-clamp-2">{grabacion.titulo}</h3>
                    {grabacion.descripcion && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{grabacion.descripcion}</p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        <span>{grabacion.fecha_creacion ? new Date(grabacion.fecha_creacion).toLocaleDateString('es-UY') : 'Sin fecha'}</span>
                      </div>
                      
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {user?.rol === 'admin' && (
                          <button
                            onClick={() => handleDeleteGrabacion(grabacion.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Añadir Grabación */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-purple-600" />
              Añadir Grabación
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Título *</label>
              <Input
                value={newGrabacion.titulo}
                onChange={(e) => setNewGrabacion({ ...newGrabacion, titulo: e.target.value })}
                placeholder="Título de la grabación"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">URL del Video o Playlist *</label>
              <Input
                value={newGrabacion.url}
                onChange={(e) => setNewGrabacion({ ...newGrabacion, url: e.target.value })}
                placeholder="https://youtube.com/watch?v=... o playlist?list=..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Puedes pegar el link de un video individual o de una playlist completa de YouTube
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Descripción</label>
              <textarea
                value={newGrabacion.descripcion}
                onChange={(e) => setNewGrabacion({ ...newGrabacion, descripcion: e.target.value })}
                placeholder="Descripción breve..."
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Duración</label>
                <Input
                  value={newGrabacion.duracion}
                  onChange={(e) => setNewGrabacion({ ...newGrabacion, duracion: e.target.value })}
                  placeholder="ej: 45:30"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Categoría</label>
                <select
                  value={newGrabacion.categoria}
                  onChange={(e) => setNewGrabacion({ ...newGrabacion, categoria: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  {categorias.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSaveGrabacion}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Reproductor de Video */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <div className="relative">
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            {selectedVideo && (
              <div>
                {/* Video Player */}
                <div className="bg-black">
                  {renderEmbeddedPlayer(selectedVideo.url)}
                </div>
                
                {/* Info */}
                <div className="p-4">
                  <h2 className="text-xl font-bold text-gray-800 mb-2">{selectedVideo.titulo}</h2>
                  {selectedVideo.descripcion && (
                    <p className="text-gray-600 mb-3">{selectedVideo.descripcion}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {selectedVideo.fecha_creacion ? new Date(selectedVideo.fecha_creacion).toLocaleDateString('es-UY') : 'Sin fecha'}
                    </span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                      {categorias.find(c => c.value === selectedVideo.categoria)?.label || 'Video'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GraduadosGrabaciones;
